import { useCallback, useEffect, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { Layout } from "../components/Layout";
import { LoadingSpinner, PageLoader } from "../components/LoadingSpinner";
import { useAuth } from "../contexts/AuthContext";
import { dbSelect } from "../lib/api";
import { ArrowLeft, Cpu, ExternalLink, RefreshCw, Search } from "lucide-react";
import { MiniAppType } from "../lib/supabase";

const ADMIN_EMAIL = "a.burkov@gmail.com";

interface NonAdminMiniApp {
  id: string;
  name: string;
  type: MiniAppType | null;
  admin_type_override: MiniAppType | null;
  slug: string | null;
  public_id: string | null;
  cloud_run_job_name: string | null;
  cloud_run_cpu: string | null;
  cloud_run_memory: string | null;
  profiles: {
    is_admin: boolean;
    email: string;
    display_name: string | null;
  } | null;
}

function getEffectiveType(project: NonAdminMiniApp): MiniAppType {
  return (project.admin_type_override || project.type ||
    "utility") as MiniAppType;
}

export function AdminMiniAppsPage() {
  const { user, session, loading: authLoading } = useAuth();
  const [miniApps, setMiniApps] = useState<NonAdminMiniApp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    document.title = 'Admin Mini-Apps - Llambada';
    return () => { document.title = 'Llambada'; };
  }, []);

  const fetchMiniApps = useCallback(async () => {
    if (!session) return;
    setLoading(true);
    setError(null);

    try {
      const { data, error: queryError } = await dbSelect<NonAdminMiniApp[]>(
        "projects",
        {
          token: session.access_token,
          select:
            "id,name,type,admin_type_override,slug,public_id,cloud_run_job_name,cloud_run_cpu,cloud_run_memory,profiles!projects_user_id_fkey(is_admin,email,display_name)",
          filters: { cloud_run_job_name: "not.is.null" },
          order: "created_at.desc",
        },
      );

      if (queryError) throw queryError;

      const transformed = (data || []).map((project) => ({
        ...project,
        profiles: Array.isArray((project as any).profiles)
          ? (project as any).profiles[0] || null
          : (project as any).profiles,
      }));

      setMiniApps(
        transformed.filter((project) => project.profiles?.is_admin !== true),
      );
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Failed to fetch mini-apps",
      );
    } finally {
      setLoading(false);
    }
  }, [session]);

  useEffect(() => {
    if (user?.email === ADMIN_EMAIL) {
      fetchMiniApps();
    }
  }, [user, fetchMiniApps]);

  if (authLoading) return <PageLoader />;

  if (!user || user.email !== ADMIN_EMAIL) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link
            to="/admin"
            className="flex h-10 w-10 items-center justify-center rounded-lg bg-theme-surface hover:bg-theme-bg-muted transition-colors"
            title="Back to Admin"
          >
            <ArrowLeft className="h-5 w-5 text-theme-text-muted" />
          </Link>
          <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-emerald-500/10 text-emerald-500">
            <Cpu className="h-6 w-6" />
          </div>
          <div className="flex-1">
            <h1 className="font-display text-2xl font-bold text-theme-text">
              Non-Admin Mini-Apps
            </h1>
            <p className="text-theme-text-secondary text-sm">
              Built mini-apps owned by non-admin users with runtime resource
              settings.
            </p>
          </div>
          <button
            onClick={fetchMiniApps}
            className="btn btn-secondary"
            disabled={loading}
          >
            {loading
              ? <LoadingSpinner size="sm" />
              : <RefreshCw className="h-4 w-4" />}
            Refresh
          </button>
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-theme-error-border bg-theme-error-bg px-4 py-3 text-sm text-theme-error">
            {error}
          </div>
        )}

        {loading
          ? (
            <div className="card p-10 flex justify-center">
              <LoadingSpinner />
            </div>
          )
          : miniApps.length === 0
          ? (
            <div className="card p-10 text-center text-theme-text-muted">
              No non-admin mini-apps found.
            </div>
          )
          : (
            <div className="card overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full min-w-[980px]">
                  <thead className="bg-theme-surface border-b border-theme-border-muted">
                    <tr>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                        Mini-App
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                        Type
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                        CPU
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                        Memory
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                        Public Page
                      </th>
                      <th className="text-left px-4 py-3 text-xs font-semibold uppercase tracking-wide text-theme-text-muted">
                        File Inspector
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {miniApps.map((project) => {
                      const effectiveType = getEffectiveType(project);
                      const publicPath = project.public_id
                        ? `/p/${project.public_id}${
                          project.slug ? `/${project.slug}` : ""
                        }`
                        : null;
                      const inspectorPath =
                        `/admin/files?projectId=${project.id}`;

                      return (
                        <tr
                          key={project.id}
                          className="border-b border-theme-border-muted/60 last:border-b-0"
                        >
                          <td className="px-4 py-3">
                            <div className="font-medium text-theme-text">
                              {project.name}
                            </div>
                            <div className="text-xs text-theme-text-muted mt-0.5">
                              {project.profiles?.display_name ||
                                project.profiles?.email || "Unknown owner"}
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <span className="inline-flex rounded-full border border-theme-border-muted px-2 py-0.5 text-xs font-medium text-theme-text">
                              {effectiveType}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-theme-text">
                            {project.cloud_run_cpu || ""}
                          </td>
                          <td className="px-4 py-3 text-sm text-theme-text">
                            {project.cloud_run_memory || ""}
                          </td>
                          <td className="px-4 py-3">
                            {publicPath
                              ? (
                                <Link
                                  to={publicPath}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1.5 text-sm text-accent-400 hover:text-accent-300"
                                >
                                  Open
                                  <ExternalLink className="h-3.5 w-3.5" />
                                </Link>
                              )
                              : (
                                <span className="text-sm text-theme-text-muted">
                                  Not published
                                </span>
                              )}
                          </td>
                          <td className="px-4 py-3">
                            <Link
                              to={inspectorPath}
                              className="inline-flex items-center gap-1.5 text-sm text-blue-400 hover:text-blue-300"
                            >
                              Open Inspector
                              <Search className="h-3.5 w-3.5" />
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
      </div>
    </Layout>
  );
}
